import { cn } from '@client/lib';
import { FundingPolicyType } from '@shared/appModel';
import { Check } from 'lucide-react';
import { PiSparkle } from 'react-icons/pi';

export interface PolicyTypeCategory {
  id: FundingPolicyType;
  name: string;
  description: string;
}

export const POLICY_TYPE_CATEGORIES: PolicyTypeCategory[] = [
  {
    id: FundingPolicyType.longTermCareInsurance,
    name: 'Traditional LTC Insurance',
    description: 'Dedicated long-term care coverage with comprehensive benefits.',
  },
  {
    id: FundingPolicyType.lifeInsuranceWithRider,
    name: 'Life Insurance with LTC Rider',
    description: 'Life policy that lets you tap death benefit early for care needs.',
  },
  {
    id: FundingPolicyType.hybridLifeInsurance,
    name: 'Asset-Based Hybrid Policy',
    description: 'Combines life insurance with LTC benefits for guaranteed returns.',
  },
  {
    id: FundingPolicyType.annuityHybrid,
    name: 'Annuity-Based Hybrid Policy',
    description: 'Combines annuity with LTC benefits for guaranteed returns.',
  },
  {
    id: FundingPolicyType.shortTermCareInsurance,
    name: 'Short-Term Care Insurance',
    description: 'Covers shorter care periods at lower cost.',
  },
];

interface PolicyTypeCardProps {
  policyType: PolicyTypeCategory;
  isSelected: boolean;
  isPredicted: boolean;
  onToggle: () => void;
  className?: string;
}

export function PolicyTypeCard({
  policyType,
  isSelected,
  isPredicted,
  onToggle,
  className,
}: PolicyTypeCardProps) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className={cn(
        'group flex w-full items-start gap-4 rounded-xl border-2 p-5 text-left transition-all',
        {
          'border-primary bg-primary/5 shadow-sm': isSelected,
          'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50/50': !isSelected,
        },
        className,
      )}
    >
      {/* Checkbox */}
      <div
        className={cn(
          'mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md border-2 transition-colors',
          {
            'border-primary bg-primary': isSelected,
            'border-gray-300 bg-white group-hover:border-gray-400': !isSelected,
          },
        )}
      >
        {isSelected && (
          <Check
            className="h-4 w-4 text-white"
            strokeWidth={3}
          />
        )}
      </div>

      {/* Content */}
      <div className="min-w-0 flex-1">
        <div className="flex items-start gap-2">
          <h3
            className={cn('text-base font-semibold leading-tight', {
              'text-gray-900': isSelected,
              'text-gray-800': !isSelected,
            })}
          >
            {policyType.name}
          </h3>
          {isPredicted && (
            <span className="bg-primary/10 text-primary inline-flex shrink-0 items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium">
              <PiSparkle className="h-3 w-3" />
              Predicted Preference
            </span>
          )}
        </div>
        <p
          className={cn('mt-1.5 text-sm leading-relaxed', {
            'text-gray-600': isSelected,
            'text-gray-500': !isSelected,
          })}
        >
          {policyType.description}
        </p>
      </div>
    </button>
  );
}
